import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import RegistrationFlow from "@/components/RegistrationFlow";
import SiteAnnouncementPopup from "@/components/SiteAnnouncementPopup";
import {
  GraduationCap, User, Award, Shield, Menu, X, CheckCircle2, ArrowRight,
  School, BookOpen, Star, Calendar, ChevronDown, Sparkles, ShieldCheck,
  Download, MapPin, Megaphone, Facebook, Instagram, Youtube, Mail, MessageCircle, Link2, Phone,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1555899434-94d1368aa7af?auto=format&fit=crop&w=1600&q=60";
const DEFAULT_FOOTER_CONTENT = {
  social_links: [
    {
      platform: "facebook",
      label: "Facebook",
      url: "https://www.facebook.com/baznasbazis",
    },
    {
      platform: "instagram",
      label: "Instagram",
      url: "https://www.instagram.com/mdj.baznasbazisdki?stkn=bXc2NjVkcWY2dHZz",
    },
    {
      platform: "youtube",
      label: "YouTube",
      url: "https://www.youtube.com/@BAZNASBAZIST",
    },
  ],
  contact: {
    title: "Hubungi Kami",
    items: [
      {
        type: "address",
        label: "Alamat",
        value: "Gedung Graha Mental Spiritual Lt.5, Jl. Awaludin II, Kebon Melati, Tanah Abang, Jakarta Pusat",
        url: "",
      },
      {
        type: "whatsapp",
        label: "Hotline Kampus",
        value: "0853-5318-7574",
        url: "https://wa.me/6285353187574",
      },
      {
        type: "whatsapp",
        label: "Hotline Mahasiswa",
        value: "0822-2869-2697",
        url: "https://wa.me/6282228692697",
      },
      {
        type: "email",
        label: "Email",
        value: "pendaftaranmdj@baznasbazisdki.id",
        url: "mailto:pendaftaranmdj@baznasbazisdki.id",
      },
    ],
  },
};
const newsSlug = (value = "") => value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const landingPopupKey = (announcement) => [
  "mdj",
  "landing-popup",
  announcement.date || "",
  announcement.category || "",
  announcement.title || "",
].join(":");

const formatLandingDate = (value, abbreviated = false) => {
  if (!value) {
    return "—";
  }
  const normalizedValue = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `${value}T12:00:00+07:00`
    : value;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const formatted = new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: abbreviated ? "short" : "long",
    year: "numeric",
    timeZone: "Asia/Jakarta",
  }).format(date);
  return abbreviated ? formatted.replace(/\bSep\b/, "Sept") : formatted;
};

const ResponsiveDate = ({ value }) => (
  <>
    <span className="hidden xl:inline">{formatLandingDate(value)}</span>
    <span className="xl:hidden">{formatLandingDate(value, true)}</span>
  </>
);

const socialIcon = (platform) => {
  if (platform === "facebook") return Facebook;
  if (platform === "instagram") return Instagram;
  if (platform === "youtube") return Youtube;
  return Link2;
};

const contactIcon = (type) => {
  if (type === "address") return MapPin;
  if (type === "email") return Mail;
  if (type === "phone") return Phone;
  if (type === "whatsapp") return MessageCircle;
  return Link2;
};

const contactHref = (item) => {
  if (item.url) return item.url;
  if (item.type === "email") return `mailto:${item.value}`;
  if (item.type === "phone") return `tel:${item.value.replace(/[^0-9+]/g, "")}`;
  if (item.type === "whatsapp") {
    const digits = item.value.replace(/\D/g, "");
    const normalized = digits.startsWith("0") ? `62${digits.slice(1)}` : digits;
    return normalized ? `https://wa.me/${normalized}` : "";
  }
  return "";
};

const NAV_LINKS = [
  { name: "Beranda", href: "#beranda" },
  { name: "Tentang", href: "#tentang" },
  { name: "Persyaratan", href: "#persyaratan" },
  { name: "Alur", href: "#alur" },
  { name: "Pengumuman", href: "#pengumuman" },
  { name: "FAQ", href: "#faq" },
  { name: "Dashboard", href: "/dashboard-publik" },
  { name: "Tutorial", href: "/tutorial" },
];

const BENEFITS = [
  { title: "Bantuan Biaya UKT", desc: "Meringankan biaya pendidikan melalui bantuan Uang Kuliah Tunggal (UKT).", icon: GraduationCap },
  { title: "Pembentukan Karakter", desc: "Pembinaan soft skill, kepemimpinan, integritas, dan etika melalui Kaderisasi MDJ.", icon: User },
  { title: "Pelatihan Hardskill & Vokasi", desc: "Pelatihan bersertifikasi (Data Analyst, Digital Marketing, Urban Farming, dll).", icon: Award },
  { title: "Kerelawanan & Pengabdian", desc: "Mendorong kontribusi sosial nyata bagi Jakarta melalui program MDJ Mengabdi.", icon: Shield },
];

const CATEGORIES = [
  { title: "Mahasiswa Sarjana (S1)", desc: "Bantuan biaya pendidikan untuk mahasiswa S1 ber-KTP atau kampus di wilayah DKI Jakarta.", icon: School },
  { title: "Mahasiswa Vokasi", desc: "Program link & match industri untuk mahasiswa vokasi dengan dukungan Super Tax Deduction.", icon: BookOpen },
  { title: "Koordinator Akademik", desc: "Jalur kepemimpinan bagi penerima manfaat untuk menjadi penggerak komunitas di tiap kota.", icon: Star },
  { title: "Volunteer / Relawan", desc: "Kesempatan berkontribusi dalam event sosial dan ke-BAZNAS-an.", icon: User },
];

const Logo = ({ light, logoUrl }) => (
  <div className="flex items-center gap-2.5" data-testid="mdj-logo">
    {logoUrl ? (
      <div className="h-10 w-10 overflow-hidden rounded-lg bg-[#27AE60] shadow-md shadow-[#27AE60]/30">
        <img src={logoUrl} alt="Logo MDJ Scholarship" className="h-full w-full object-contain" data-testid="mdj-header-logo-image" />
      </div>
    ) : (
      <>
        <div className="h-10 w-10 overflow-hidden rounded-lg bg-[#27AE60] shadow-md shadow-[#27AE60]/30">
        <span className="flex h-full w-full items-center justify-center text-xs font-black text-white">
          MDJ
        </span>
        </div>
        <div className="leading-tight">
          <p className={`font-display font-extrabold text-base ${light ? "text-white" : "text-[#1F2937]"}`}>MDJ Scholarship</p>
          <p className={`text-[10px] font-semibold ${light ? "text-white/70" : "text-[#6B7280]"}`}>BAZNAS (BAZIS) DKI Jakarta</p>
        </div>
      </>
    )}
  </div>
);

const Navbar = ({ onNavigate, logoUrl }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  return (
    <nav className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Logo light={!scrolled} logoUrl={logoUrl} />
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <a key={l.name} href={l.href} data-testid={`nav-${l.name.toLowerCase()}`}
                className={`px-3.5 py-2 text-sm font-semibold rounded-lg transition-colors ${scrolled ? "text-[#6B7280] hover:text-[#0B6B3A] hover:bg-[#E8F6EE]" : "text-white/90 hover:text-white hover:bg-white/10"}`}>
                {l.name}
              </a>
            ))}
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => onNavigate("/login")}
              data-testid="nav-login-btn"
              className={`px-4 py-2 text-sm font-bold transition-colors ${scrolled ? "text-[#0B6B3A] hover:bg-[#E8F6EE]" : "text-white hover:bg-white/10"}`}
            >
              Masuk
            </button>
            <button
              onClick={() => onNavigate("/register")}
              data-testid="nav-register-btn"
              className="rounded-xl bg-[#27AE60] px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-[#0B6B3A]/20 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#0B6B3A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F2C94C] focus-visible:ring-offset-2"
            >
              Daftar
            </button>
          </div>
          <button className={`lg:hidden p-2 rounded-lg ${scrolled ? "text-[#1F2937]" : "text-white"}`} onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-1">
          {NAV_LINKS.map((l) => (
            <a key={l.name} href={l.href} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.name.toLowerCase()}`} className="block px-3 py-2.5 text-sm font-semibold text-[#1F2937] rounded-lg hover:bg-[#E8F6EE]">{l.name}</a>
          ))}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={() => onNavigate("/login")}
              data-testid="nav-mobile-login-btn"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-[#0B6B3A]"
            >
              Masuk
            </button>
            <button
              onClick={() => onNavigate("/register")}
              data-testid="nav-mobile-register-btn"
              className="w-full rounded-xl bg-[#27AE60] px-4 py-2.5 text-sm font-bold text-white"
            >
              Daftar
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [faqOpen, setFaqOpen] = useState(0);
  const [remainingTime, setRemainingTime] = useState(null);
  const [landingPopup, setLandingPopup] = useState(null);
  const go = (p) => navigate(p);

  useEffect(() => {
    axios.get(`${API}/site/content`).then((r) => setContent(r.data)).catch(() => setContent({}));
  }, []);

  const c = content || {};
  const settings = c.settings || {};
  const stats = c.stats || [];
  const registrationFlow = c.registration_flow || [];
  const footerContent = c.footer || DEFAULT_FOOTER_CONTENT;
  const socialLinks = footerContent.social_links || DEFAULT_FOOTER_CONTENT.social_links;
  const contact = footerContent.contact || DEFAULT_FOOTER_CONTENT.contact;
  const contactItems = contact.items || DEFAULT_FOOTER_CONTENT.contact.items;
  const announcements = c.announcements || [];
  const activeLandingPopup = announcements.find(
    (announcement) => announcement.show_landing_popup === true,
  );
  const requirements = c.eligibility_requirements || c.requirements || [];
  const requiredDocuments = c.required_documents || [];
  const faqs = c.faqs || [];
  const hero = c.hero || {};
  const bannerBg = c.banner_url ? `${API.replace("/api", "")}${c.banner_url}` : HERO_IMG;
  const aboutBg = c.about_url ? `${API.replace("/api", "")}${c.about_url}` : null;
  const logoUrl = c.logo_url ? `${API}/site/logo` : null;
  const recipientQuota = Number.parseInt(
    String(settings.recipient_quota || "").replace(/\D/g, ""),
    10,
  );
  const hasRecipientQuota = Number.isFinite(recipientQuota) && recipientQuota > 0;
  const registrationIsOpen = settings.registration_open && (!settings.registration_start_at || Date.now() >= new Date(settings.registration_start_at).getTime()) && (!settings.registration_end_at || Date.now() < new Date(settings.registration_end_at).getTime());

  useEffect(() => {
    const updateRemainingTime = () => {
      const end = settings.registration_end_at ? new Date(settings.registration_end_at) : null;
      const distance = end ? Math.max(0, end.getTime() - Date.now()) : 0;
      setRemainingTime({
        days: Math.floor(distance / 86400000),
        hours: Math.floor((distance % 86400000) / 3600000),
        minutes: Math.floor((distance % 3600000) / 60000),
        seconds: Math.floor((distance % 60000) / 1000),
      });
    };
    updateRemainingTime();
    const timer = window.setInterval(updateRemainingTime, 1000);
    return () => window.clearInterval(timer);
  }, [settings.registration_end_at]);

  useEffect(() => {
    if (!activeLandingPopup) {
      setLandingPopup(null);
      return;
    }
    const popupId = landingPopupKey(activeLandingPopup);
    if (window.localStorage.getItem(popupId)) {
      setLandingPopup(null);
      return;
    }
    setLandingPopup({
      ...activeLandingPopup,
      id: popupId,
      message: activeLandingPopup.summary,
    });
  }, [activeLandingPopup]);

  const dismissLandingPopup = (popupId) => {
    window.localStorage.setItem(popupId, "seen");
    setLandingPopup(null);
  };

  const viewLandingPopup = () => {
    const announcementTitle = landingPopup?.title;
    if (landingPopup?.id) {
      dismissLandingPopup(landingPopup.id);
    }
    if (announcementTitle) {
      go(`/berita/${newsSlug(announcementTitle)}`);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar onNavigate={go} logoUrl={logoUrl} />
      <SiteAnnouncementPopup
        announcement={landingPopup}
        onDismiss={dismissLandingPopup}
        onView={viewLandingPopup}
        variant="landing"
      />

      {/* HERO */}
      <section id="beranda" className="relative min-h-[620px] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#0B6B3A]">
          <img src={bannerBg} alt="Jakarta" fetchpriority="high" decoding="async" onError={(e) => { if (e.currentTarget.src !== HERO_IMG) e.currentTarget.src = HERO_IMG; }} className="w-full h-full object-cover" data-testid="hero-banner-img" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B3D1F]/95 via-[#0B6B3A]/85 to-[#27AE60]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-2 gap-10 items-center py-16">
          <div className="text-white animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/15 backdrop-blur text-xs font-bold border border-white/20 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#F2C94C]" /> Program Beasiswa Pendidikan DKI Jakarta
            </span>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5">
              {hero.title || "Wujudkan Pendidikan dan Masa Depan Terbaikmu"}
            </h1>
            <p className="text-base lg:text-lg text-white/85 leading-relaxed max-w-xl mb-8">
              {hero.subtitle || "Program Masa Depan Jakarta (MDJ) hadir sebagai wujud nyata dukungan BAZNAS (BAZIS) Provinsi DKI Jakarta. MDJ Scholarship Circle of Ummah"}
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => go("/login")} data-testid="hero-register-btn"
                className="px-8 py-3.5 text-base font-bold text-[#0B6B3A] bg-[#F2C94C] rounded-full hover:bg-[#D4AC2B] shadow-lg shadow-black/20 transition-colors flex items-center gap-2">
                Daftar Beasiswa <ArrowRight className="w-5 h-5" />
              </button>
              <a href="#tentang" className="px-8 py-3.5 text-base font-bold text-white bg-white/10 border border-white/30 rounded-full hover:bg-white/20 transition-colors">Pelajari Program</a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-white/80">
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#F2C94C]" /> Gratis & transparan</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#F2C94C]" /> Terverifikasi & akuntabel</span>
            </div>
          </div>
          <div className="hidden lg:flex justify-end animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 w-full max-w-sm shadow-2xl border border-white/50">
              <p
                className="font-display font-extrabold text-lg text-[#1F2937]"
                data-testid="registration-summary-title"
              >
                Masa Depan Jakarta Scholarship Tahun {settings.year || "2027"}
              </p>
              <p
                className="mb-5 mt-1 text-sm text-[#6B7280]"
                data-testid="registration-summary-description"
              >
                Segera daftarkan diri anda sebelum tenggat waktu berakhir.
              </p>
              <div className="rounded-xl bg-[#E8F6EE] p-4 flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${registrationIsOpen ? "bg-[#27AE60] animate-pulse" : "bg-[#DC2626]"}`} />
                <div>
                  <p className="text-xs text-[#6B7280]">Status Pendaftaran</p>
                  <p className="font-bold text-[#0B6B3A] text-sm">{registrationIsOpen ? "Sedang Dibuka" : "Ditutup"}</p>
                </div>
              </div>
              <div className="mt-4 space-y-2.5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex shrink-0 items-center gap-2 text-[#6B7280]">
                    <Calendar className="h-4 w-4" /> Periode
                  </span>
                  <span
                    className="text-right font-semibold text-[#1F2937]"
                    data-testid="registration-period-value"
                  >
                    {formatLandingDate(
                      settings.registration_start_at || settings.period_start,
                      true,
                    )}
                    {" – "}
                    {formatLandingDate(
                      settings.registration_end_at || settings.period_end,
                      true,
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex shrink-0 items-center gap-2 text-[#6B7280]">
                    <Megaphone className="h-4 w-4" /> Pengumuman
                  </span>
                  <span
                    className="text-right font-semibold text-[#1F2937]"
                    data-testid="registration-announcement-date-value"
                  >
                    <ResponsiveDate value={settings.announcement_date} />
                  </span>
                </div>
                {hasRecipientQuota && (
                  <div
                    className="flex items-center justify-between gap-3"
                    data-testid="recipient-quota-display"
                  >
                    <span className="flex shrink-0 items-center gap-2 text-[#6B7280]">
                      <User className="h-4 w-4" /> Kuota Penerima
                    </span>
                    <span className="text-right font-semibold text-[#1F2937]">
                      {new Intl.NumberFormat("id-ID").format(recipientQuota)} Mahasiswa
                    </span>
                  </div>
                )}
              </div>
              {registrationIsOpen && settings.registration_end_at && remainingTime && (
                <div className="mt-5 border-t border-gray-100 pt-4" data-testid="registration-countdown">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#0B6B3A]">Pendaftaran berakhir dalam</p>
                  <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                    {[[remainingTime.days, "Hari"], [remainingTime.hours, "Jam"], [remainingTime.minutes, "Menit"], [remainingTime.seconds, "Detik"]].map(([value, label]) => <div key={label} className="bg-[#E8F6EE] px-1 py-2"><p className="font-display text-lg font-black text-[#0B6B3A]">{String(value).padStart(2, "0")}</p><p className="text-[10px] font-bold text-[#6B7280]">{label}</p></div>)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0B6B3A] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center" data-testid={`stat-${i}`}>
              <p className="font-display font-black text-3xl lg:text-5xl text-[#F2C94C]">{s.value}</p>
              <p className="text-xs lg:text-sm text-white/80 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="tentang" className="py-20 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`mb-16 grid items-center gap-12 ${aboutBg ? "lg:grid-cols-2" : ""}`}
        >
          <div>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight text-[#1F2937] mb-4">Tentang Program Masa Depan Jakarta</h2>
            <p className="text-[#6B7280] leading-relaxed">Program Masa Depan Jakarta (MDJ) adalah inisiatif pemberdayaan dan bantuan biaya pendidikan (UKT) yang dikelola oleh BAZNAS (BAZIS) Provinsi DKI Jakarta. Lebih dari sekadar beasiswa, kami membangun karakter, kompetensi vokasi, dan kepemimpinan generasi muda Jakarta.</p>
            <div className="flex flex-wrap gap-2 mt-6">
              {["Berilmu", "Berakhlak", "Berdaya", "Berkontribusi"].map((t) => (
                <span key={t} className="px-3.5 py-1.5 rounded-full bg-[#E8F6EE] text-[#0B6B3A] text-xs font-bold">{t}</span>
              ))}
            </div>
          </div>
          {aboutBg && (
            <img
              src={aboutBg}
              alt="Mahasiswa berkolaborasi"
              className="h-72 w-full rounded-2xl object-cover shadow-lg"
              data-testid="about-program-image"
            />
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {BENEFITS.map((b, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-[0_4px_20px_-2px_rgba(39,174,96,0.06)] hover:-translate-y-1 transition-transform">
              <div className="w-12 h-12 rounded-xl bg-[#E8F6EE] flex items-center justify-center mb-4"><b.icon className="w-6 h-6 text-[#27AE60]" /></div>
              <h3 className="font-display font-bold text-lg text-[#1F2937] mb-2">{b.title}</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20 lg:py-24 bg-[#0B3D1F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight text-white text-center mb-3">Pilih Kategori Program</h2>
          <p className="text-white/70 text-center max-w-2xl mx-auto mb-12">Jalur kepesertaan disesuaikan dengan jenjang pendidikan dan fokus pengembangan.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((cat, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#F2C94C]/20 flex items-center justify-center mb-4"><cat.icon className="w-6 h-6 text-[#F2C94C]" /></div>
                <h3 className="font-display font-bold text-lg text-white mb-2">{cat.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed mb-4">{cat.desc}</p>
                <button onClick={() => go("/register")} className="text-sm font-bold text-[#F2C94C] flex items-center gap-2 hover:gap-3 transition-all mt-auto w-fit">Lihat Persyaratan <ArrowRight className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REQUIREMENTS */}
      <section id="persyaratan" className="py-20 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight text-[#1F2937] mb-4">Syarat & Kelengkapan Berkas</h2>
            <p className="text-[#6B7280] leading-relaxed mb-6">Pastikan Anda memenuhi ketentuan dan menyiapkan dokumen sebelum memulai pendaftaran.</p>
            <div className="rounded-2xl bg-[#FEF9E7] border border-[#F2C94C]/40 p-4 text-sm text-[#7a5c00]">Dokumen tidak lengkap dapat menyebabkan pendaftaran tidak dapat diproses.</div>
          </div>
          <div className="lg:col-span-2 grid gap-6 md:grid-cols-2">
            <RequirementList title="Syarat (Kriteria / Ketentuan Pendaftar)" items={requirements} testId="eligibility-requirements" />
            <RequirementList title="Kelengkapan Berkas (Dokumen yang Harus Disiapkan)" items={requiredDocuments} testId="required-documents" />
          </div>
        </div>
      </section>

      <RegistrationFlow steps={registrationFlow} />

      {/* ANNOUNCEMENTS */}
      <section id="pengumuman" className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-[#1F2937] lg:text-4xl">Berita Terbaru</h2>
            <p className="mt-2 text-sm text-[#6B7280]">Informasi terkini seputar MDJ Scholarship dan kegiatan BAZNAS (BAZIS).</p>
          </div>
          <Link to="/berita" data-testid="view-all-news-link" className="text-sm font-bold text-[#0B6B3A] hover:text-[#27AE60]">Lihat Selengkapnya →</Link>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-3 mdj-scrollbar" data-testid="latest-announcements-row">
          {announcements.map((a, i) => (
            <article key={i} className="w-56 shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-[0_4px_16px_-4px_rgba(31,41,55,0.18)] transition-transform hover:-translate-y-1" data-testid={`latest-announcement-${i}`}>
              <div className="relative aspect-[1.35] overflow-hidden bg-[#E8F6EE]">
                {a.banner?.url ? <img src={`${API.replace("/api", "")}${a.banner.url}`} alt={`Banner ${a.title}`} className="h-full w-full object-cover" data-testid={`announcement-banner-${i}`} /> : <div className="flex h-full items-center justify-center px-5 text-center text-xs font-extrabold text-[#0B6B3A]">{a.category || "Pengumuman MDJ"}</div>}
                {a.date && <span className="absolute left-2 top-2 rounded bg-white/95 px-2 py-1 text-[10px] font-extrabold text-[#0B6B3A]">{a.date}</span>}
              </div>
              <div className="p-4">
                <span className="text-[10px] font-bold text-[#0B6B3A]">{a.category}</span>
                <h3 className="mt-1 line-clamp-2 font-display text-base font-extrabold leading-snug text-[#1F2937]">{a.title}</h3>
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-[#6B7280]">{a.summary}</p>
                <div className="mt-3 flex flex-wrap gap-3 text-xs font-bold text-[#0B6B3A]">
                  <Link
                    to={`/berita/${newsSlug(a.title)}`}
                    data-testid={`read-latest-news-${i}`}
                  >
                    Baca Selengkapnya →
                  </Link>
                  {a.attachment?.url && (
                    <a
                      href={`${API.replace("/api", "")}${a.attachment.url}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`announcement-attachment-${i}`}
                    >
                      Lihat Berkas
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 lg:py-24 bg-[#F9FAFB]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight text-[#1F2937] text-center mb-3">Pertanyaan yang Sering Diajukan</h2>
          <p className="text-[#6B7280] text-center mb-10">Temukan jawaban untuk pertanyaan umum seputar program.</p>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button onClick={() => setFaqOpen(faqOpen === i ? -1 : i)} data-testid={`faq-${i}`}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left">
                  <span className="font-semibold text-[#1F2937]">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#27AE60] shrink-0 transition-transform ${faqOpen === i ? "rotate-180" : ""}`} />
                </button>
                {faqOpen === i && <div className="px-5 pb-5 text-sm text-[#6B7280] leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b-2 border-[#27AE60] bg-[#08743D] py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="font-display mb-4 text-3xl font-black leading-[1.04] text-white sm:text-4xl">
            Siap Mengambil Langkah untuk Masa Depanmu?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-sm leading-relaxed text-white/90 sm:text-base">
            Persiapkan dokumenmu, lengkapi pendaftaran, dan jadilah bagian dari Kader Akademia
            Program Masa Depan Jakarta.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => go("/register")}
              data-testid="cta-register-btn"
              className="rounded-xl bg-[#F2C94C] px-7 py-3 text-sm font-bold text-[#0B6B3A] shadow-lg
                shadow-black/10 transition-colors duration-200 hover:bg-[#D4AC2B]
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white
                focus-visible:ring-offset-2 focus-visible:ring-offset-[#08743D]"
            >
              Daftar Sekarang
            </button>
            <a
              href="#persyaratan"
              data-testid="cta-requirements-link"
              className="rounded-xl border border-white/30 px-7 py-3 text-sm font-bold text-white
                transition-colors duration-200 hover:bg-white/10 focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2
                focus-visible:ring-offset-[#08743D]"
            >
              Cek Persyaratan
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#111827] py-14 text-white/65 sm:py-16">
        <div
          className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-[1.45fr_0.9fr_1fr_1.4fr]
            lg:gap-x-6"
        >
          <div>
            <p className="font-display text-base font-extrabold uppercase text-white">
              Masa Depan Jakarta Scholarship
            </p>
            <p className="mt-0.5 text-[10px] font-medium text-white/50">
              BAZNAS (BAZIS) Provinsi DKI Jakarta
            </p>
            <p className="mt-5 max-w-xs text-sm leading-relaxed">
              Program beasiswa dan pembinaan komprehensif bagi mahasiswa DKI Jakarta dan keluarga
              prasejahtera untuk membangun generasi berilmu, berakhlak, berdaya, dan berkontribusi.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="footer-social-links">
              {socialLinks.filter((item) => item?.label && item?.url).map((item, index) => {
                const Icon = socialIcon(item.platform);
                const platform = item.platform || "other";
                const testId = ["facebook", "instagram", "youtube"].includes(platform)
                  ? `footer-${platform}-link`
                  : `footer-social-${index}`;
                return (
                  <a
                    key={`${platform}-${index}`}
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={item.label}
                    data-testid={testId}
                    className="text-white/60 transition-colors duration-200 hover:text-[#F2C94C]"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <p className="font-display text-sm font-bold text-white">Tautan Cepat</p>
            <ul className="mt-5 space-y-3 text-sm">
              {NAV_LINKS.slice(1).map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                    className="transition-colors duration-200 hover:text-white"
                  >
                    {link.name === "Tentang" ? "Tentang Program" : link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-display text-sm font-bold text-white">Informasi Legal</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <Link
                  to="/privacy-policy"
                  data-testid="footer-privacy-link"
                  className="transition-colors duration-200 hover:text-white"
                >
                  Kebijakan Privasi
                </Link>
              </li>
              <li>
                <Link
                  to="/syarat-ketentuan"
                  data-testid="footer-terms-link"
                  className="transition-colors duration-200 hover:text-white"
                >
                  Syarat dan Ketentuan
                </Link>
              </li>
              <li>
                <Link
                  to="/pedoman-program"
                  data-testid="footer-guide-link"
                  className="transition-colors duration-200 hover:text-white"
                >
                  Pedoman Program
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-display text-sm font-bold text-white" data-testid="footer-contact-title">
              {contact.title || "Hubungi Kami"}
            </p>
            <div className="mt-5 space-y-3 text-sm leading-relaxed" data-testid="footer-contact-list">
              {contactItems.filter((item) => item?.label && item?.value).map((item, index) => {
                const Icon = contactIcon(item.type);
                const href = contactHref(item);
                const content = (
                  <>
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#27AE60]" />
                    <span className="min-w-0 whitespace-pre-line">
                      <strong className="font-semibold text-white/85">{item.label}: </strong>
                      {item.value}
                    </span>
                  </>
                );
                const className = "flex min-w-0 items-start gap-3 transition-colors duration-200 hover:text-white";
                if (!href) {
                  return <p key={`${item.label}-${index}`} data-testid={`footer-contact-${index}`} className={className}>{content}</p>;
                }
                return (
                  <a
                    key={`${item.label}-${index}`}
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noreferrer" : undefined}
                    aria-label={item.label}
                    data-testid={`footer-contact-${index}`}
                    className={className}
                  >
                    {content}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mx-auto mt-12 max-w-6xl border-t border-white/10 px-4 pt-6 sm:px-6">
          <div className="flex flex-col justify-between gap-2 text-xs sm:flex-row sm:items-center">
            <p data-testid="footer-copyright">
              © {new Date().getFullYear()} BAZNAS (BAZIS) Provinsi DKI Jakarta. Seluruh hak dilindungi.
            </p>
            <p data-testid="footer-tagline">MDJ Scholarship Circle of Ummah</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function RequirementList({ title, items, testId }) {
  return <section className="border border-gray-100 bg-white p-5 shadow-sm" data-testid={testId}>
    <h3 className="font-display text-lg font-extrabold text-[#1F2937]">{title}</h3>
    <div className="mt-4 space-y-3">
      {items.map((item, index) => <div key={`${item}-${index}`} className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#27AE60]" /><span className="text-sm leading-relaxed text-[#1F2937]">{item}</span></div>)}
    </div>
  </section>;
}
